<template>
  <div class="patients-page">
    <div class="vm-page-header">
      <div>
        <h1>Pacientes</h1>
        <span class="vm-page-header__sub">
          Pacientes vinculados à sua agenda
        </span>
      </div>
    </div>

    <div class="card patients-card">
      <!-- Abas -->
      <div class="tab-bar">
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'future' }"
          @click="setTab('future')"
        >
          Próximos pacientes
        </button>
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'past' }"
          @click="setTab('past')"
        >
          Já atendidos
        </button>
      </div>

      <!-- Busca -->
      <div class="patients-search">
        <div class="patients-search__wrap">
          <svg
            class="patients-search__icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M21 21l-4.35-4.35"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <input
            v-model="search"
            class="patients-search__input"
            type="text"
            placeholder="Buscar pelo nome..."
          />
        </div>
      </div>

      <!-- Carregando -->
      <div v-if="loading" class="patients-state">
        <b-spinner style="width:28px;height:28px;color:var(--color-primary)" />
        <span class="patients-state__text">Carregando...</span>
      </div>

      <!-- Vazio -->
      <div v-else-if="!filtered.length" class="patients-state">
        <svg
          width="36"
          height="36"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="9"
            cy="8"
            r="3.5"
            stroke="var(--color-gray-300)"
            stroke-width="2"
          />
          <path
            d="M2 20c0-3.5 3-5.5 7-5.5s7 2 7 5.5"
            stroke="var(--color-gray-300)"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <span class="patients-state__text">
          {{
            activeTab === "future"
              ? "Nenhum paciente para atender."
              : "Não há pacientes atendidos."
          }}
        </span>
      </div>

      <!-- Tabela -->
      <div v-else class="patients-table-wrap">
        <table class="patients-table">
          <thead>
            <tr>
              <th>Paciente</th>
              <th>Data</th>
              <th>Hora</th>
              <th>Ficha médica</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="appt in paginated" :key="appt.id">
              <td>
                <div class="pt-patient">
                  <div class="avatar avatar--sm">
                    <img
                      v-if="appt.patient.photo"
                      :src="appt.patient.photo"
                      :alt="appt.patient.name"
                    />
                    <span v-else>{{ initials(appt.patient.name) }}</span>
                  </div>
                  <span class="pt-name">{{ appt.patient.name }}</span>
                </div>
              </td>
              <td class="pt-mono">{{ formatDate(appt.scheduled_at) }}</td>
              <td class="pt-mono">{{ formatTime(appt.scheduled_at) }}</td>
              <td>
                <button
                  class="btn btn--outline btn--sm"
                  @click="goToDetail(appt)"
                >
                  Ver detalhes
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Rodapé: paginação simples -->
      <div v-if="filtered.length > perPage" class="patients-footer">
        <span class="patients-footer__count">
          Mostrando
          <b>{{ paginated.length }}</b>
          de
          <b>{{ filtered.length }}</b>
        </span>
        <button
          v-if="page * perPage < filtered.length"
          class="btn--link"
          @click="page++"
        >
          Ver mais
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Appointments } from "../services/appointments";

export default {
  name: "DoctorPatientsView",

  data() {
    return {
      activeTab: "future",
      appointments: [],
      loading: false,
      search: "",
      page: 1,
      perPage: 10
    };
  },

  mounted() {
    this.fetchAppointments();
  },

  methods: {
    async setTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.search = "";
      this.page = 1;
      await this.fetchAppointments();
    },

    async fetchAppointments() {
      this.loading = true;
      this.appointments = [];
      try {
        const res = await Appointments.fetchFiltered("doctors", this.activeTab);
        this.appointments = res.data || [];
      } catch {
        this.appointments = [];
      } finally {
        this.loading = false;
      }
    },

    goToDetail(appt) {
      this.$router.push({
        name: "DoctorPatientDetail",
        params: { appointmentId: appt.id },
        query: { patientId: appt.patient.id }
      });
    },

    initials(name) {
      return (
        (name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || "?"
      );
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },

    formatTime(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  },

  computed: {
    filtered() {
      const q = this.search.toLowerCase().trim();
      const list = q
        ? this.appointments.filter(a =>
            (a.patient.name || "").toLowerCase().includes(q)
          )
        : this.appointments;
      if (this.activeTab !== "past") return list;
      return [...list].sort(
        (a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)
      );
    },

    paginated() {
      return this.filtered.slice(0, this.page * this.perPage);
    }
  },

  watch: {
    search() {
      this.page = 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.patients-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.patients-card {
  padding: 0;
  overflow: hidden;
}

/* Abas */
.tab-bar {
  display: flex;
  gap: var(--space-1);
  padding: 0 var(--space-6);
  border-bottom: 2px solid var(--color-gray-200);
}

.tab-btn {
  padding: var(--space-4) var(--space-5);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: var(--color-primary);
  }
  &--active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }
}

/* Busca */
.patients-search {
  padding: var(--space-4) var(--space-6);
  border-bottom: 1px solid var(--color-gray-100);

  &__wrap {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    max-width: 380px;
    height: 40px;
    padding: 0 var(--space-4);
    border: 1.5px solid var(--color-gray-200);
    border-radius: var(--radius-lg);
    background: var(--color-bg-page, #f4f6fb);
    transition: border-color 0.15s;

    &:focus-within {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(26, 108, 247, 0.1);
    }
  }

  &__icon {
    flex-shrink: 0;
    color: var(--color-gray-400);
    pointer-events: none;
  }

  &__input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: var(--font-size-sm);
    color: var(--color-gray-900);
    outline: none;

    &::placeholder {
      color: var(--color-gray-400);
    }
  }
}

/* Estado */
.patients-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-12) var(--space-6);

  &__text {
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
  }
}

/* Tabela */
.patients-table-wrap {
  overflow-x: auto;
}

.patients-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    padding: var(--space-3) var(--space-5);
    background: var(--color-bg-page, #f4f6fb);
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    color: var(--color-gray-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid var(--color-gray-200);
  }

  tbody tr {
    border-bottom: 1px solid var(--color-gray-100);
    transition: background 0.1s;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: var(--color-bg-page, #f4f6fb);
    }
  }

  tbody td {
    padding: var(--space-4) var(--space-5);
    font-size: var(--font-size-sm);
    color: var(--color-gray-800);
    vertical-align: middle;
    white-space: nowrap;
  }
}

.pt-patient {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.pt-name {
  font-weight: var(--fw-semibold);
  color: var(--color-gray-900);
}

.pt-mono {
  font-family: var(--font-num, "Inter", sans-serif);
  color: var(--color-gray-700);
}

/* Rodapé */
.patients-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-100);

  &__count {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);

    b {
      color: var(--color-gray-800);
    }
  }
}

.btn--link {
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
}
</style>
